/**
 * The products Velto actually sells, defined once.
 *
 * Velto does not sell memberships. PodPlay lists exactly these four packs, all
 * on the same packages page, in this order. Every field below is
 * owner-confirmed. Do not add a claim here that isn't on the PodPlay product
 * listing — that is why `value` is optional: PodPlay states a comparison value
 * for some packs and not others, so the cards only show one where it does.
 *
 * Consumed by the homepage Packages section, the packages landing page's
 * product block, and the homepage offer catalog (JSON-LD), so the three can
 * never drift apart. Route the URL through withAttribution() at the point of
 * use — see src/lib/outbound.ts — so gclid/UTM forwarding survives.
 */

export type Pack = {
  /** Stable key for React lists and analytics event names. */
  id: string;
  name: string;
  /** Display price, e.g. "$250". */
  price: string;
  /** Bare decimal for schema.org Offer.price, e.g. "250.00". */
  priceValue: string;
  /** Qualifier shown beside the price, e.g. "one-time". */
  cadence: string;
  /** Comparison value shown beside the price, e.g. "$450 value". Only set
   *  when the PodPlay listing states one. */
  value?: string;
  includes: string[];
  /** Validity claim, rendered as the last bullet. */
  expiry: string;
};

export const PACKS_URL = "https://velto.podplay.app/packages/nyc-brooklyn";

/**
 * Both pack buttons say the same short thing. The pack name used to be in the
 * label ("Buy the All Access 10+2 Open Play Pack"), which was too long to fit
 * the pill on a narrow phone — the card title carries the name instead.
 */
export const PACK_CTA_LABEL = "Grab This Pack";

/**
 * Accessible name for a pack's CTA. Both buttons now read identically on
 * screen, so the pack name has to live here: it is what a screen reader
 * announces, and what a link-list shows when the link is read out of context.
 *
 * It leads with the visible label verbatim because WCAG 2.5.3 (Label in Name,
 * level A) requires the accessible name to contain the visible text — a
 * speech-input user says "click Grab This Pack", and a bare
 * "Grab the <name>" would not match.
 */
export const packCtaAriaLabel = (pack: Pack) =>
  `${PACK_CTA_LABEL} — ${pack.name}`;

export const packs: Pack[] = [
  {
    id: "twenty-one-pack",
    name: "All Access 21 Open Play Pack",
    price: "$445",
    priceValue: "445.00",
    cadence: "one-time",
    includes: [
      "21 All Access Open Play Passes (including DUPR matches and Coach Guided)",
    ],
    expiry: "Expires 6 Months After Purchase",
  },
  {
    // Kept as "ten-pack" through the rename: the id feeds the analytics event
    // name (pack_ten_pack_click), so changing it would break event continuity.
    id: "ten-pack",
    // PodPlay's checkout page calls this simply "10-Pack". The longer name here
    // is deliberate and owner-confirmed — do not "correct" it to match checkout.
    name: "All Access 10+2 Open Play Pack",
    price: "$265",
    priceValue: "265.00",
    cadence: "one-time",
    value: "$450 value",
    includes: [
      "10 All Access Open Play Passes (including DUPR matches and Coach Guided)",
      "2 All Access Open Play Guest Passes",
    ],
    expiry: "Expires 6 Months After Purchase",
  },
  {
    id: "beginners-pack",
    name: "Velto Beginners Pack",
    price: "$365",
    priceValue: "365.00",
    cadence: "one-time",
    value: "$600 value",
    includes: [
      "5 All Access Open Play Passes (including DUPR and Coach Guided)",
      "4 Clinics Passes (can be used for a clinic series)",
      "1 Velto House Paddle",
    ],
    expiry: "Expires 6 Months After Purchase",
  },
  {
    id: "court-hours-pack",
    name: "Buy 5 Court Hours Get 1 Free",
    price: "$550",
    priceValue: "550.00",
    cadence: "one-time",
    includes: [
      "6 Private Court Hours (Peak/Off-Peak)",
      "100 Replay Credits",
    ],
    expiry: "Expires 6 Months After Purchase",
  },
];
